import type { Component } from 'vue'
import AboutEditorial from './blocks/about/AboutEditorial.vue'
import AboutPanel from './blocks/about/AboutPanel.vue'
import BookingSection from './blocks/booking/BookingSection.vue'
import BusinessHoursSection from './blocks/business-hours/BusinessHoursSection.vue'
import ContactDetails from './blocks/contact/ContactDetails.vue'
import ContactPanel from './blocks/contact/ContactPanel.vue'
import CtaCentered from './blocks/cta/CtaCentered.vue'
import CtaSplit from './blocks/cta/CtaSplit.vue'
import FaqAccordion from './blocks/faq/FaqAccordion.vue'
import FaqColumns from './blocks/faq/FaqColumns.vue'
import FooterCentered from './blocks/footer/FooterCentered.vue'
import FooterColumns from './blocks/footer/FooterColumns.vue'
import FeaturesGrid from './blocks/features/FeaturesGrid.vue'
import FeaturesRows from './blocks/features/FeaturesRows.vue'
import HeaderCentered from './blocks/header/HeaderCentered.vue'
import HeaderClassic from './blocks/header/HeaderClassic.vue'
import HeaderSplit from './blocks/header/HeaderSplit.vue'
import HeroCentered from './blocks/hero/HeroCentered.vue'
import HeroSplit from './blocks/hero/HeroSplit.vue'
import HeroSpotlight from './blocks/hero/HeroSpotlight.vue'
import MapSplit from './blocks/map/MapSplit.vue'
import QuoteRequestSection from './blocks/quote/QuoteRequestSection.vue'
import FormLayoutBlock from './blocks/form/FormLayoutBlock.vue'
import ReviewsFeatured from './blocks/reviews/ReviewsFeatured.vue'
import ReviewsGrid from './blocks/reviews/ReviewsGrid.vue'
import ServicesCards from './blocks/services/ServicesCards.vue'
import ServicesNumbered from './blocks/services/ServicesNumbered.vue'
import ServiceAreaChecker from './blocks/service-area/ServiceAreaChecker.vue'
import WorkGalleryBeforeAfter from './blocks/work-gallery/WorkGalleryBeforeAfter.vue'
import WorkGalleryEditorial from './blocks/work-gallery/WorkGalleryEditorial.vue'
import WorkGalleryFeatured from './blocks/work-gallery/WorkGalleryFeatured.vue'
import WorkGalleryGrid from './blocks/work-gallery/WorkGalleryGrid.vue'
import type {
  AboutVariant,
  BookingVariant,
  BusinessHoursVariant,
  ContactVariant,
  CtaVariant,
  FaqVariant,
  FeaturesVariant,
  FooterVariant,
  HeaderVariant,
  HeroVariant,
  MapVariant,
  QuoteVariant,
  ReviewsVariant,
  ServicesVariant,
  ServiceAreaVariant,
  WebsiteBlock,
  WorkGalleryVariant,
  FormVariant,
} from './types'

export type BlockCategory = 'marketing' | 'business-information' | 'utility'

export interface BlockVariantDefinition {
  id: string
  name: string
  description: string
  component: Component
}

export interface BlockDefinition {
  type: WebsiteBlock['type']
  name: string
  category: BlockCategory
  description: string
  variants: Record<string, BlockVariantDefinition>
}

const headerVariants: Record<HeaderVariant, BlockVariantDefinition> = {
  classic: {
    id: 'classic',
    name: 'Classic',
    description: 'Brand left, navigation centered, and action on the right.',
    component: HeaderClassic,
  },
  centered: {
    id: 'centered',
    name: 'Centered Brand',
    description: 'A prominent centered brand with balanced navigation and action areas.',
    component: HeaderCentered,
  },
  split: {
    id: 'split',
    name: 'Bold Split',
    description: 'A dark navigation bar with a strongly separated action area.',
    component: HeaderSplit,
  },
}

const heroVariants: Record<HeroVariant, BlockVariantDefinition> = {
  centered: {
    id: 'centered',
    name: 'Centered Welcome',
    description: 'Centered content with an expansive supporting image.',
    component: HeroCentered,
  },
  split: {
    id: 'split',
    name: 'Split Service',
    description: 'Business copy and imagery arranged side by side.',
    component: HeroSplit,
  },
  spotlight: {
    id: 'spotlight',
    name: 'Image Spotlight',
    description: 'Content layered over a full-width image for a bold introduction.',
    component: HeroSpotlight,
  },
}

const featuresVariants: Record<FeaturesVariant, BlockVariantDefinition> = {
  grid: {
    id: 'grid',
    name: 'Quiet Grid',
    description: 'Calm feature cards that give every strength equal emphasis.',
    component: FeaturesGrid,
  },
  rows: {
    id: 'rows',
    name: 'Editorial Rows',
    description: 'A refined numbered list with generous space for each advantage.',
    component: FeaturesRows,
  },
}

const servicesVariants: Record<ServicesVariant, BlockVariantDefinition> = {
  cards: {
    id: 'cards',
    name: 'Service Cards',
    description: 'A polished grid that gives every service its own clear space.',
    component: ServicesCards,
  },
  numbered: {
    id: 'numbered',
    name: 'Numbered List',
    description: 'An editorial service index with strong scanning and hierarchy.',
    component: ServicesNumbered,
  },
}

const aboutVariants: Record<AboutVariant, BlockVariantDefinition> = {
  editorial: {
    id: 'editorial',
    name: 'Editorial Split',
    description: 'A balanced story and image layout with supporting business facts.',
    component: AboutEditorial,
  },
  panel: {
    id: 'panel',
    name: 'Story Panel',
    description: 'A confident dark panel with a focused visual and compact facts.',
    component: AboutPanel,
  },
}

const reviewsVariants: Record<ReviewsVariant, BlockVariantDefinition> = {
  grid: {
    id: 'grid',
    name: 'Review Grid',
    description: 'Balanced review cards that give every customer voice equal weight.',
    component: ReviewsGrid,
  },
  featured: {
    id: 'featured',
    name: 'Featured Quote',
    description: 'One leading review supported by a quieter set of customer quotes.',
    component: ReviewsFeatured,
  },
}

const faqVariants: Record<FaqVariant, BlockVariantDefinition> = {
  accordion: {
    id: 'accordion',
    name: 'Calm Accordion',
    description: 'Accessible expandable answers beside a concise introduction.',
    component: FaqAccordion,
  },
  columns: {
    id: 'columns',
    name: 'Answer Columns',
    description: 'Open answers arranged in a simple two-column reference layout.',
    component: FaqColumns,
  },
}

const ctaVariants: Record<CtaVariant, BlockVariantDefinition> = {
  centered: {
    id: 'centered',
    name: 'Centered Invitation',
    description: 'A contained, high-focus invitation with centered actions.',
    component: CtaCentered,
  },
  split: {
    id: 'split',
    name: 'Split Callout',
    description: 'A full-width message with actions clearly separated to the side.',
    component: CtaSplit,
  },
}

const contactVariants: Record<ContactVariant, BlockVariantDefinition> = {
  panel: {
    id: 'panel',
    name: 'Contact Panel',
    description: 'A premium contained layout with the main message and contact details.',
    component: ContactPanel,
  },
  details: {
    id: 'details',
    name: 'Direct Details',
    description: 'A spacious contact directory designed for immediate scanning.',
    component: ContactDetails,
  },
}

const mapVariants: Record<MapVariant, BlockVariantDefinition> = {
  split: {
    id: 'split',
    name: 'Map Split',
    description: 'Business details beside an interactive Google Map.',
    component: MapSplit,
  },
}

const bookingVariants: Record<BookingVariant, BlockVariantDefinition> = {
  inline: {
    id: 'inline',
    name: 'Inline Scheduler',
    description: 'Business context beside an embedded appointment scheduler.',
    component: BookingSection,
  },
  callout: {
    id: 'callout',
    name: 'Booking Callout',
    description: 'A focused booking invitation that opens the provider securely.',
    component: BookingSection,
  },
}

const quoteVariants: Record<QuoteVariant, BlockVariantDefinition> = {
  form: {
    id: 'form',
    name: 'Quote Form',
    description: 'Introductory content beside a complete service quote form.',
    component: QuoteRequestSection,
  },
  compact: {
    id: 'compact',
    name: 'Quick Quote',
    description: 'A focused quote request inside a premium contained panel.',
    component: QuoteRequestSection,
  },
}

const formVariants: Record<FormVariant, BlockVariantDefinition> = {
  centered: {
    id: 'centered',
    name: 'Centered Form',
    description: 'A focused form inside a centered contained panel with supporting copy.',
    component: FormLayoutBlock,
  },
  split: {
    id: 'split',
    name: 'Split Form',
    description: 'Introductory content on one side with the form on the other.',
    component: FormLayoutBlock,
  },
  minimal: {
    id: 'minimal',
    name: 'Minimal Form',
    description: 'A compact form integrated directly into the page without a heavy panel.',
    component: FormLayoutBlock,
  },
}

const businessHoursVariants: Record<BusinessHoursVariant, BlockVariantDefinition> = {
  schedule: {
    id: 'schedule',
    name: 'Weekly Schedule',
    description: 'A clear seven-day schedule beside helpful business context.',
    component: BusinessHoursSection,
  },
  card: {
    id: 'card',
    name: 'Hours Card',
    description: 'A compact, centered hours panel for quick visitor reference.',
    component: BusinessHoursSection,
  },
}

const serviceAreaVariants: Record<ServiceAreaVariant, BlockVariantDefinition> = {
  inline: {
    id: 'inline',
    name: 'Inline Checker',
    description: 'Service-area context beside a focused availability checker.',
    component: ServiceAreaChecker,
  },
  card: {
    id: 'card',
    name: 'Service Area Card',
    description: 'A centered checker with visible coverage areas and a compact footprint.',
    component: ServiceAreaChecker,
  },
}

const workGalleryVariants: Record<WorkGalleryVariant, BlockVariantDefinition> = {
  grid: {
    id: 'grid',
    name: 'Project Grid',
    description: 'A balanced gallery that gives each completed project equal emphasis.',
    component: WorkGalleryGrid,
  },
  'before-after': {
    id: 'before-after',
    name: 'Before and After',
    description: 'Paired project images that make transformations immediately clear.',
    component: WorkGalleryBeforeAfter,
  },
  featured: {
    id: 'featured',
    name: 'Featured Project',
    description: 'One leading project supported by a compact collection of additional work.',
    component: WorkGalleryFeatured,
  },
  editorial: {
    id: 'editorial',
    name: 'Editorial Gallery',
    description: 'A premium staggered composition with varied image proportions.',
    component: WorkGalleryEditorial,
  },
}

const footerVariants: Record<FooterVariant, BlockVariantDefinition> = {
  columns: {
    id: 'columns',
    name: 'Column Directory',
    description: 'A complete footer with brand context and grouped navigation.',
    component: FooterColumns,
  },
  centered: {
    id: 'centered',
    name: 'Centered Close',
    description: 'A minimal centered footer for smaller professional websites.',
    component: FooterCentered,
  },
}

export const blockRegistry: Record<WebsiteBlock['type'], BlockDefinition> = {
  header: {
    type: 'header',
    name: 'Header',
    category: 'utility',
    description: 'Site identity, navigation, and primary action.',
    variants: headerVariants,
  },
  hero: {
    type: 'hero',
    name: 'Hero',
    category: 'marketing',
    description: 'Introduces the business and guides visitors to the primary action.',
    variants: heroVariants,
  },
  features: {
    type: 'features',
    name: 'Features',
    category: 'marketing',
    description: 'Explains the qualities and advantages that make a business easy to choose.',
    variants: featuresVariants,
  },
  services: {
    type: 'services',
    name: 'Services',
    category: 'marketing',
    description: 'Explains the services a business offers and guides visitors to act.',
    variants: servicesVariants,
  },
  about: {
    type: 'about',
    name: 'About',
    category: 'marketing',
    description: 'Builds familiarity through the business story, image, and facts.',
    variants: aboutVariants,
  },
  reviews: {
    type: 'reviews',
    name: 'Reviews',
    category: 'marketing',
    description: 'Presents customer voices and trust signals.',
    variants: reviewsVariants,
  },
  faq: {
    type: 'faq',
    name: 'FAQ',
    category: 'marketing',
    description: 'Answers common questions and reduces uncertainty.',
    variants: faqVariants,
  },
  cta: {
    type: 'cta',
    name: 'Call to Action',
    category: 'marketing',
    description: 'Creates a focused next step near the end of a page.',
    variants: ctaVariants,
  },
  contact: {
    type: 'contact',
    name: 'Contact',
    category: 'marketing',
    description: 'Shows direct ways to reach or book the business.',
    variants: contactVariants,
  },
  map: {
    type: 'map',
    name: 'Map',
    category: 'business-information',
    description: 'Shows a business location or service area with an embedded Google Map.',
    variants: mapVariants,
  },
  booking: {
    type: 'booking',
    name: 'Booking',
    category: 'business-information',
    description: 'Lets visitors schedule an appointment through a booking provider.',
    variants: bookingVariants,
  },
  quote: {
    type: 'quote',
    name: 'Quote Request',
    category: 'business-information',
    description: 'Collects the details needed to prepare a service estimate.',
    variants: quoteVariants,
  },
  'business-hours': {
    type: 'business-hours',
    name: 'Business Hours',
    category: 'business-information',
    description: 'Shows when the business is open and when appointments are available.',
    variants: businessHoursVariants,
  },
  'service-area': {
    type: 'service-area',
    name: 'Service Area Checker',
    category: 'business-information',
    description: 'Lets visitors check whether a business serves their location.',
    variants: serviceAreaVariants,
  },
  'work-gallery': {
    type: 'work-gallery',
    name: 'Work Gallery',
    category: 'marketing',
    description: 'Shows completed projects, visual transformations, and proof of craftsmanship.',
    variants: workGalleryVariants,
  },
  form: {
    type: 'form',
    name: 'Form',
    category: 'business-information',
    description: 'Renders a reusable form built in the dashboard with a chosen layout.',
    variants: formVariants,
  },
  footer: {
    type: 'footer',
    name: 'Footer',
    category: 'utility',
    description: 'Closes the page with business identity and supporting navigation.',
    variants: footerVariants,
  },
}

export function resolveBlockVariant(block: WebsiteBlock): BlockVariantDefinition | undefined {
  return blockRegistry[block.type]?.variants[block.variant]
}
